import {
  defineComponent,
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
  computed,
  useAttrs,
  nextTick,
  getCurrentInstance,
} from "vue";
import { geocodeForward } from "@/services/naaviApi/internalService";
import { requestBrowserLocation } from "@/helpers/geolocationHelper";

const DEFAULT_INPUT_CLASS = "location-autocomplete__input pac-target-input";
const DEFAULT_WRAPPER_CLASS = "sm:w-full lg:w-6/12";
const NEARBY_ATTR = "data-market-nearby-id";

type LocationValue = {
  address?: string | null;
  lat?: number | null;
  lng?: number | null;
  placeId?: string | null;
} | null;
type GooglePlaceLocation = { lat: () => number; lng: () => number };
type GooglePlaceResult = {
  formatted_address?: string;
  name?: string;
  place_id?: string;
  geometry?: { location?: GooglePlaceLocation | null } | null;
};
type GoogleAutocomplete = {
  getPlace: () => GooglePlaceResult;
  setComponentRestrictions?: (restrictions: {
    country: string | string[];
  }) => void;
};
type GoogleMapsEventListener = { remove?: () => void } | unknown;
type GoogleMapsEvent = {
  addListener: (
    instance: GoogleAutocomplete,
    eventName: string,
    handler: () => void,
  ) => GoogleMapsEventListener;
  removeListener: (listener: GoogleMapsEventListener) => void;
};
type GoogleMapsPlaces = {
  Autocomplete: new (
    input: HTMLInputElement,
    options?: Record<string, unknown>,
  ) => GoogleAutocomplete;
};
type GoogleMaps = {
  places?: GoogleMapsPlaces;
  event?: GoogleMapsEvent;
};
type WindowWithGoogle = Window & { google?: { maps?: GoogleMaps } };

export default defineComponent({
  name: "LocationAutocomplete",
  inheritAttrs: false,
  props: {
    modelValue: {
      type: Object as () => LocationValue,
      default: null,
    },
    placeholder: {
      type: String,
      default: "Search",
    },
    countries: {
      type: Array as () => string[] | undefined,
      default: undefined,
    },
    language: {
      type: String,
      default: "",
    },
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const inputRef = ref<HTMLInputElement | null>(null);
    const autocompleteRef = ref<GoogleAutocomplete | null>(null);
    const googleListenerRef = ref<GoogleMapsEventListener | null>(null);
    const observers = ref<MutationObserver[]>([]);
    const inputValue = ref<string>(props.modelValue?.address ?? "");
    const isLocating = ref(false);
    const attrs = useAttrs();
    const instance = getCurrentInstance();
    const containerSetupAttempts = ref(0);
    const autocompleteInitAttempts = ref(0);
    const observedContainers = new WeakSet<HTMLElement>();
    const componentMarker = `market-nearby-${Date.now().toString(
      16,
    )}-${Math.random().toString(16).slice(2)}`;
    const inputPlaceholder = computed(() => props.placeholder);

    const userLanguage = computed(() => {
      if (props.language && props.language.trim().length > 0) {
        return props.language;
      }
      return typeof navigator !== "undefined" ? navigator.language : "es-ES";
    });

    const translate = (key: string): string => {
      const translateFn = instance?.proxy?.$t as
        | ((value: string) => string)
        | undefined;
      if (typeof translateFn === "function") {
        return translateFn(key);
      }
      return key;
    };

    const buildClassFromObject = (
      classObject: Record<string, unknown>,
    ): string => {
      return Object.entries(classObject)
        .filter(([, value]) => Boolean(value))
        .map(([key]) => key)
        .join(" ");
    };

    const wrapperClass = computed(() => {
      const incoming = attrs.class;
      if (!incoming) {
        return DEFAULT_WRAPPER_CLASS;
      }

      if (typeof incoming === "string") {
        return `${DEFAULT_WRAPPER_CLASS} ${incoming}`.trim();
      }

      if (Array.isArray(incoming)) {
        return [DEFAULT_WRAPPER_CLASS, ...incoming].join(" ");
      }

      if (typeof incoming === "object") {
        const extra = buildClassFromObject(incoming as Record<string, unknown>);
        return extra
          ? `${DEFAULT_WRAPPER_CLASS} ${extra}`.trim()
          : DEFAULT_WRAPPER_CLASS;
      }

      return DEFAULT_WRAPPER_CLASS;
    });

    const inputAttrs = computed(() => {
      const result: Record<string, unknown> = {};
      Object.entries(attrs).forEach(([key, value]) => {
        if (key !== "class") {
          result[key] = value;
        }
      });
      return result;
    });

    const inputClass = computed(() => DEFAULT_INPUT_CLASS);

    const emitLocation = (payload: {
      address: string;
      lat: number | null;
      lng: number | null;
      placeId?: string | null;
    }) => {
      inputValue.value = payload.address;
      if (inputRef.value) {
        inputRef.value.value = payload.address;
      }
      emit("update:modelValue", {
        address: payload.address,
        lat: payload.lat,
        lng: payload.lng,
        placeId: payload.placeId ?? null,
      });
    };

    const fallbackGeocode = async (
      address: string,
      placeId?: string | null,
    ) => {
      try {
        const response = await geocodeForward({
          fullAddress: address,
          language: userLanguage.value,
        });
        const data = response;
        if (
          data &&
          typeof data.latitude === "number" &&
          typeof data.longitude === "number"
        ) {
          emitLocation({
            address: data.formattedAddress || address,
            lat: data.latitude,
            lng: data.longitude,
            placeId: placeId ?? data.placeId ?? null,
          });
        } else {
          emitLocation({
            address,
            lat: NaN,
            lng: NaN,
            placeId: placeId ?? null,
          });
        }
      } catch {
        emitLocation({ address, lat: NaN, lng: NaN, placeId: placeId ?? null });
      }
    };

    const handlePlaceChanged = async () => {
      const googleObj = (window as WindowWithGoogle).google;
      if (!autocompleteRef.value || !googleObj?.maps?.places) {
        return;
      }

      const place = autocompleteRef.value.getPlace();
      if (!place) {
        return;
      }

      const formattedAddress =
        place.formatted_address || place.name || inputRef.value?.value || "";
      if (place.geometry?.location) {
        const latitude = place.geometry.location.lat();
        const longitude = place.geometry.location.lng();
        emitLocation({
          address: formattedAddress,
          lat: latitude,
          lng: longitude,
          placeId: place.place_id ?? null,
        });
      } else if (formattedAddress) {
        await fallbackGeocode(formattedAddress, place.place_id ?? null);
      }
    };

    const getValidCountries = (): string[] => {
      if (!Array.isArray(props.countries)) {
        return [];
      }
      return props.countries
        .map((country) => (typeof country === "string" ? country.trim() : ""))
        .filter((country) => country.length > 0);
    };

    const applyComponentRestrictions = () => {
      const autocomplete = autocompleteRef.value;
      if (
        !autocomplete ||
        typeof autocomplete.setComponentRestrictions !== "function"
      ) {
        return;
      }
      const validCountries = getValidCountries();
      if (validCountries.length === 0) {
        return;
      }
      const restriction =
        validCountries.length === 1 ? validCountries[0] : validCountries;
      autocomplete.setComponentRestrictions({ country: restriction });
    };

    const ensureAutocomplete = () => {
      if (autocompleteRef.value || typeof window === "undefined") {
        return;
      }
      const googleObj = (window as WindowWithGoogle).google;
      if (
        !googleObj ||
        !googleObj.maps ||
        !googleObj.maps.places ||
        !inputRef.value
      ) {
        if (autocompleteInitAttempts.value < 50) {
          autocompleteInitAttempts.value += 1;
          window.setTimeout(() => {
            ensureAutocomplete();
          }, 120);
        }
        return;
      }

      const options: Record<string, unknown> = {
        fields: ["geometry.location", "formatted_address", "place_id", "name"],
      };
      const validCountries = getValidCountries();
      if (validCountries.length) {
        options.componentRestrictions = {
          country:
            validCountries.length === 1 ? validCountries[0] : validCountries,
        };
      }

      const autocomplete = new googleObj.maps.places.Autocomplete(
        inputRef.value,
        options,
      );
      const mapsEvent = googleObj.maps.event;
      if (mapsEvent) {
        googleListenerRef.value = mapsEvent.addListener(
          autocomplete,
          "place_changed",
          handlePlaceChanged,
        );
      } else {
        googleListenerRef.value = null;
      }
      autocompleteRef.value = autocomplete;
      applyComponentRestrictions();
    };

    const selectCurrentLocation = async () => {
      if (isLocating.value) {
        return;
      }
      isLocating.value = true;
      const label = translate("Cerca de mí");
      if (inputRef.value) {
        inputRef.value.value = label;
      }
      inputValue.value = label;
      try {
        const location = await requestBrowserLocation({
          language: userLanguage.value,
          skipReverseGeocode: true,
        });
        if (location) {
          emitLocation({
            address: label,
            lat: location.latitude,
            lng: location.longitude,
            placeId: location.placeId ?? null,
          });
        }
      } catch (error) {
        void error;
      } finally {
        isLocating.value = false;
        nextTick(() => {
          inputRef.value?.blur();
        });
      }
    };

    const clearSelection = () => {
      emitLocation({
        address: "",
        lat: null,
        lng: null,
        placeId: null,
      });
    };

    const createNearbyElement = (container: HTMLElement) => {
      const existing = container.querySelector(
        ".pac-item-nearby",
      ) as HTMLElement | null;
      const label = translate("Cerca de mí");
      if (existing) {
        if (existing.dataset.marketComponentId === componentMarker) {
          const query = existing.querySelector(".pac-item-query");
          if (query && query.textContent !== label) {
            query.textContent = label;
          }
          return;
        }
        existing.remove();
      }

      const item = document.createElement("div");
      item.className = "pac-item pac-item-nearby";
      item.dataset.marketComponentId = componentMarker;
      const icon = document.createElement("span");
      icon.className = "pac-icon pac-icon-marker";
      const textWrapper = document.createElement("span");
      textWrapper.className = "pac-item-query";
      textWrapper.textContent = label;
      textWrapper.style.fontWeight = "600";
      item.appendChild(icon);
      item.appendChild(textWrapper);

      item.addEventListener("mousedown", (event) => {
        event.preventDefault();
        selectCurrentLocation();
      });
      item.addEventListener(
        "touchstart",
        (event) => {
          event.preventDefault();
          selectCurrentLocation();
        },
        { passive: false },
      );

      container.insertBefore(item, container.firstChild);
    };

    const observeContainer = (container: HTMLElement) => {
      createNearbyElement(container);
      if (observedContainers.has(container)) {
        return;
      }
      const observer = new MutationObserver(() => {
        createNearbyElement(container);
      });
      observer.observe(container, { childList: true });
      observers.value.push(observer);
      observedContainers.add(container);
    };

    const ensurePacContainer = (): boolean => {
      const containers = Array.from(
        document.getElementsByClassName("pac-container"),
      ) as HTMLElement[];
      if (!containers.length) {
        return false;
      }
      let attached = false;
      containers.forEach((container) => {
        const marker = container.getAttribute(NEARBY_ATTR);
        if (marker !== componentMarker) {
          container.setAttribute(NEARBY_ATTR, componentMarker);
          observeContainer(container);
        } else {
          createNearbyElement(container);
        }
        const ownItem = container.querySelector(
          `.pac-item-nearby[data-market-component-id="${componentMarker}"]`,
        );
        if (ownItem) {
          attached = true;
        }
      });
      return attached;
    };

    const schedulePacContainerSetup = () => {
      if (typeof window === "undefined") {
        return;
      }
      containerSetupAttempts.value += 1;
      const attached = ensurePacContainer();
      if (!attached && containerSetupAttempts.value < 10) {
        window.setTimeout(() => {
          schedulePacContainerSetup();
        }, 80);
      }
    };

    const handleFocus = () => {
      ensureAutocomplete();
      containerSetupAttempts.value = 0;
      autocompleteInitAttempts.value = 0;
      schedulePacContainerSetup();
    };

    const handleInput = (event: Event) => {
      const target = event.target as HTMLInputElement;
      inputValue.value = target.value;
      containerSetupAttempts.value = 0;
      schedulePacContainerSetup();
      if (!target.value.trim()) {
        emitLocation({
          address: "",
          lat: null,
          lng: null,
          placeId: null,
        });
      }
    };

    const handleKeydown = () => {
      containerSetupAttempts.value = 0;
      schedulePacContainerSetup();
    };

    watch(
      () => props.modelValue,
      (newVal) => {
        const address = newVal?.address ?? "";
        if (address !== inputValue.value) {
          inputValue.value = address;
          if (inputRef.value) {
            inputRef.value.value = address;
          }
        }
      },
      { deep: true },
    );

    watch(
      () => props.countries,
      () => {
        applyComponentRestrictions();
      },
      { deep: true },
    );

    onMounted(() => {
      if (inputRef.value) {
        inputRef.value.value = inputValue.value || "";
      }
      ensureAutocomplete();
    });

    onBeforeUnmount(() => {
      observers.value.forEach((observer) => observer.disconnect());
      observers.value = [];
      const googleObj = (window as WindowWithGoogle).google;
      if (googleObj?.maps?.event && googleListenerRef.value) {
        googleObj.maps.event.removeListener(googleListenerRef.value);
      }
      googleListenerRef.value = null;
      autocompleteRef.value = null;
      const containers = Array.from(
        document.getElementsByClassName("pac-container"),
      ) as HTMLElement[];
      containers.forEach((container) => {
        if (container.getAttribute(NEARBY_ATTR) === componentMarker) {
          container.removeAttribute(NEARBY_ATTR);
          const ownItem = container.querySelector(
            `.pac-item-nearby[data-market-component-id="${componentMarker}"]`,
          );
          if (ownItem) {
            ownItem.remove();
          }
        }
      });
    });

    return {
      inputRef,
      inputValue,
      inputClass,
      inputAttrs,
      wrapperClass,
      inputPlaceholder,
      handleFocus,
      handleInput,
      handleKeydown,
      clearSelection,
      translate,
    };
  },
});
